import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { CentreAuthProvider } from '@/context/CentreAuthContext';
import { SiteConfigProvider } from '@/context/SiteConfigContext';
import { CatalogProvider } from '@/context/CatalogContext';
import { TooltipProvider } from '@/components/ui/tooltip';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

import { ScrollToTop } from '@/components/ScrollToTop';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { AdminRoute } from '@/components/layout/AdminRoute';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { CentreOwnerRoute } from '@/components/layout/CentreOwnerRoute';
import { CentreOwnerLayout } from '@/components/centre/CentreOwnerLayout';

/* ------------------------------------------------------------------ */
/*  Lazy-loaded pages - improves Core Web Vitals (LCP, FCP, TTI)      */
/* ------------------------------------------------------------------ */
const Home = lazy(() => import('@/pages/Home'));
const Login = lazy(() => import('@/pages/Login'));
const Signup = lazy(() => import('@/pages/Signup'));
const OtpVerification = lazy(() => import('@/pages/OtpVerification'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const CompleteProfile = lazy(() => import('@/pages/CompleteProfile'));
const Legal = lazy(() => import('@/pages/Legal'));
const PathCategories = lazy(() => import('@/pages/PathCategories'));
const CategoryServices = lazy(() => import('@/pages/CategoryServices'));
const BookingDetails = lazy(() => import('@/pages/BookingDetails'));
const Knowledge = lazy(() => import('@/pages/Knowledge'));
const ArticleDetail = lazy(() => import('@/pages/ArticleDetail'));
const History = lazy(() => import('@/pages/History'));
const Profile = lazy(() => import('@/pages/Profile'));
const SavedAddresses = lazy(() => import('@/pages/SavedAddresses'));
const ReviewBooking = lazy(() => import('@/pages/ReviewBooking'));
const PackageBooking = lazy(() => import('@/pages/PackageBooking'));
const Contact = lazy(() => import('@/pages/Contact'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Centres = lazy(() => import('@/pages/Centres'));
const CentreDetail = lazy(() => import('@/pages/CentreDetail'));
const SearchResults = lazy(() => import('@/pages/SearchResults'));
const CentreSignup = lazy(() => import('@/pages/CentreSignup'));
const PartnerLanding = lazy(() => import('@/pages/PartnerLanding'));
const ServiceDetail = lazy(() => import('@/pages/ServiceDetail'));
const BookService = lazy(() => import('@/pages/BookService'));
const PackagePurchase = lazy(() => import('@/pages/PackagePurchase'));

/* Phase 2 - programmatic SEO pages */
const SeoDispatcher = lazy(() => import('@/pages/seo/SeoDispatcher'));
const Condition = lazy(() => import('@/pages/seo/Condition'));
const Recovery = lazy(() => import('@/pages/seo/Recovery'));
const NearMe = lazy(() => import('@/pages/seo/NearMe'));
const CitiesIndex = lazy(() => import('@/pages/seo/CitiesIndex'));
const OnlineConsultation = lazy(() => import('@/pages/OnlineConsultation'));

const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminCentreApprovals = lazy(() => import('@/pages/admin/AdminCentreApprovals'));
const AdminPayments = lazy(() => import('@/pages/admin/AdminPayments'));
const AdminPayouts = lazy(() => import('@/pages/admin/AdminPayouts'));
const AdminCustomers = lazy(() => import('@/pages/admin/AdminCustomers'));
const AdminCustomerDetail = lazy(() => import('@/pages/admin/AdminCustomerDetail'));
const AdminBookings = lazy(() => import('@/pages/admin/AdminBookings'));
const AdminServices = lazy(() => import('@/pages/admin/AdminServices'));
const AdminArticles = lazy(() => import('@/pages/admin/AdminArticles'));
const AdminCoupons = lazy(() => import('@/pages/admin/AdminCoupons'));
const AdminSiteConfig = lazy(() => import('@/pages/admin/AdminSiteConfig'));
const AdminNotifications = lazy(() => import('@/pages/admin/AdminNotifications'));
const AdminMarketing = lazy(() => import('@/pages/admin/AdminMarketing'));
const AdminLinkAnalytics = lazy(() => import('@/pages/admin/AdminLinkAnalytics'));
const AdminTherapyOptions = lazy(() => import('@/pages/admin/AdminTherapyOptions'));
const AdminCentres = lazy(() => import('@/pages/admin/AdminCentres'));
const AdminConsultants = lazy(() => import('@/pages/admin/AdminConsultants'));
const AdminListingPromos = lazy(() => import('@/pages/admin/AdminListingPromos'));

/* Centre-owner self-service portal */
const CentreLogin = lazy(() => import('@/pages/centre/CentreLogin'));
const CentreForgotPassword = lazy(() => import('@/pages/centre/CentreForgotPassword'));
const CentreDashboard = lazy(() => import('@/pages/centre/CentreDashboard'));
const CentreListing = lazy(() => import('@/pages/centre/CentreListing'));
const CentreServices = lazy(() => import('@/pages/centre/CentreServices'));
const CentreAvailability = lazy(() => import('@/pages/centre/CentreAvailability'));
const CentreBookings = lazy(() => import('@/pages/centre/CentreBookings'));
const CentrePatients = lazy(() => import('@/pages/centre/CentrePatients'));
const CentreReviews = lazy(() => import('@/pages/centre/CentreReviews'));
const CentrePayouts = lazy(() => import('@/pages/centre/CentrePayouts'));
const PaymentReturn = lazy(() => import('@/pages/PaymentReturn'));
const PayBridge = lazy(() => import('@/pages/PayBridge'));

/* ------------------------------------------------------------------ */
/*  Minimal loading fallback                                          */
/* ------------------------------------------------------------------ */
function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

/** Wrap only the auth pages that render Google Sign-In. Scoping the provider
 *  here (instead of the whole app) keeps the ~95KB Google gsi/client script off
 *  every public page — it now loads only on /login, /partner-signup and
 *  /partner/login (PageSpeed "reduce unused JavaScript"). */
function GoogleGate({ children }: { children: ReactNode }) {
  return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{children}</GoogleOAuthProvider>;
}

/** The partner portal moved from /centre/* to /partner/*. Redirect any old URL
 *  (bookmarks, emails, backlinks) to the new path, preserving the sub-page. */
function RedirectCentreToPartner() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/centre/, '/partner') + search + hash} replace />;
}

export default function App() {
  return (
      <AuthProvider>
        <CentreAuthProvider>
        <SiteConfigProvider>
        <CatalogProvider>
        <TooltipProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Toaster position="top-center" richColors closeButton />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Checkout host for the mobile apps (see PayBridge). Outside the
                  site layout on purpose: it opens inside the app's browser
                  sheet, where a navbar, footer, chat bubble and install prompt
                  would make a payment step look like leaving the app. */}
              <Route path="/pay" element={<PayBridge />} />
              <Route element={<Layout />}>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<GoogleGate><Login /></GoogleGate>} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/verify-otp" element={<OtpVerification />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* /terms retired — Terms live in /legal#terms; consent is captured at signup. */}
                <Route path="/terms" element={<Navigate to="/legal#terms" replace />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/contact" element={<Contact />} />
                {/* Cashfree return_url target for redirect-based methods. */}
                <Route path="/payment-return" element={<PaymentReturn />} />
                {/* Legacy /services page removed - browsing now goes
                    Home → Path → category → service. Redirect old links. */}
                <Route path="/services" element={<Navigate to="/centres" replace />} />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/path/:pathId" element={<PathCategories />} />
                <Route path="/path/:pathId/:categoryId" element={<CategoryServices />} />
                <Route path="/knowledge" element={<Knowledge />} />
                <Route path="/knowledge/:slug" element={<ArticleDetail />} />
                <Route path="/centres" element={<Centres />} />
                <Route path="/centres/:id" element={<CentreDetail />} />
                <Route path="/partners" element={<PartnerLanding />} />
                <Route path="/partner-signup" element={<GoogleGate><CentreSignup /></GoogleGate>} />
                {/* Old slug kept as a permanent redirect (bookmarks, marketing QRs, backlinks). */}
                <Route path="/centre-signup" element={<Navigate to="/partner-signup" replace />} />
                {/* Partner login + reset render with the public header/footer
                    (the authenticated portal pages below use their own layout). */}
                <Route path="/partner/login" element={<GoogleGate><CentreLogin /></GoogleGate>} />
                <Route path="/partner/forgot-password" element={<CentreForgotPassword />} />
                {/* Old /centre/* partner URLs → /partner/* (bookmarks, emails, backlinks). */}
                <Route path="/centre/*" element={<RedirectCentreToPartner />} />
                <Route path="/centres/:centreId/service/:serviceIndex" element={<ServiceDetail />} />
                <Route path="/centres/:centreId/service/:serviceIndex/book" element={<BookService />} />
                <Route path="/centres/:centreId/service/:serviceIndex/package" element={<PackagePurchase />} />

                {/* Phase 2: programmatic SEO pages.
                    React Router v7 only matches a `:param` when it
                    occupies the entire segment - `/physiotherapy-in-:rest`
                    silently doesn't match. So every URL family routes
                    through `/:slug` and SeoDispatcher figures out
                    which template to render based on the slug shape. */}
                <Route path="/near-me" element={<NearMe />} />
                <Route path="/physiotherapy-cities" element={<CitiesIndex />} />
                <Route path="/online-physiotherapy-consultation" element={<OnlineConsultation />} />
                <Route path="/online-consultation"
                       element={<Navigate to="/online-physiotherapy-consultation" replace />} />
                <Route path="/conditions/:slug" element={<Condition />} />
                <Route path="/recovery/:slug" element={<Recovery />} />
                <Route path="/:slug" element={<SeoDispatcher />} />
                {/* Nested location URLs: /physiotherapy-in-<city>/<sub-region>[/<centre-id>].
                    Static-first segments (/centres/:id, /conditions/:slug…) still
                    outrank these, so only unmatched shapes reach the dispatcher. */}
                <Route path="/:slug/:subSlug" element={<SeoDispatcher />} />
                <Route path="/:slug/:subSlug/:centreId" element={<SeoDispatcher />} />

                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/complete-profile" element={<CompleteProfile />} />
                  <Route path="/book/:serviceId" element={<BookingDetails />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/saved-addresses" element={<SavedAddresses />} />
                  <Route path="/review/:bookingId" element={<ReviewBooking />} />
                  <Route path="/package-booking" element={<PackageBooking />} />
                </Route>
              </Route>

              {/* Admin routes (outside Layout, has own AdminLayout) */}
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="payments" element={<AdminPayments />} />
                  <Route path="payouts" element={<AdminPayouts />} />
                  <Route path="customers" element={<AdminCustomers />} />
                  <Route path="customers/:email" element={<AdminCustomerDetail />} />
                  <Route path="bookings" element={<AdminBookings />} />
                  <Route path="services" element={<AdminServices />} />
                  <Route path="articles" element={<AdminArticles />} />
                  <Route path="coupons" element={<AdminCoupons />} />
                  <Route path="site-config" element={<AdminSiteConfig />} />
                  <Route path="notifications" element={<AdminNotifications />} />
                  <Route path="therapy-options" element={<AdminTherapyOptions />} />
                  <Route path="centres" element={<AdminCentres />} />
                  <Route path="consultants" element={<AdminConsultants />} />
                  <Route path="centre-approvals" element={<AdminCentreApprovals />} />
                  <Route path="listing-promos" element={<AdminListingPromos />} />
                  <Route path="marketing" element={<AdminMarketing />} />
                  <Route path="marketing/:slug" element={<AdminLinkAnalytics />} />
                </Route>
              </Route>

              {/* Partner portal (separate auth, own sidebar layout) */}
              <Route element={<CentreOwnerRoute />}>
                <Route path="/partner" element={<CentreOwnerLayout />}>
                  <Route index element={<Navigate to="/partner/dashboard" replace />} />
                  <Route path="dashboard" element={<CentreDashboard />} />
                  <Route path="listing" element={<CentreListing />} />
                  <Route path="services" element={<CentreServices />} />
                  <Route path="availability" element={<CentreAvailability />} />
                  <Route path="bookings" element={<CentreBookings />} />
                  <Route path="patients" element={<CentrePatients />} />
                  <Route path="reviews" element={<CentreReviews />} />
                  <Route path="payouts" element={<CentrePayouts />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
        </CatalogProvider>
        </SiteConfigProvider>
        </CentreAuthProvider>
      </AuthProvider>
  );
}
